import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import {
  CalendarDays,
  Newspaper,
  Megaphone,
  Shield,
  UsersRound,
  CalendarClock,
  GraduationCap,
} from "lucide-react";

import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";
import { db } from "../web/src/firebaseClient";

export default function AdminPanel() {
  const [access, setAccess] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const authUser = getAuth().currentUser;

        if (!authUser) {
          setAccess({
            isAdmin: false,
            isMentor: false,
            isInstructor: false,
            isSupervisor: false,
            canAccess: false,
          });
          return;
        }

        // Admin rolü Firebase custom claim üzerinden kontrol ediliyor
        const token = await authUser.getIdTokenResult();
        const isAdmin = token.claims.role === "admin";

        let isMentor = false;
        let isInstructor = false;
        let isSupervisor = false;

        // Kullanıcının Firestore'daki ek rollerini al
        const usersSnapshot = await getDocs(
          query(
            collection(db, "users"),
            where("email", "==", authUser.email)
          )
        );

        if (!usersSnapshot.empty) {
          const userData = usersSnapshot.docs[0].data();

          isMentor = userData.roles?.mentor === true;
          isInstructor = userData.roles?.instructor === true;
          isSupervisor = userData.roles?.supervisor === true;
        }

        setAccess({
          isAdmin,
          isMentor,
          isInstructor,
          isSupervisor,
          canAccess:
            isAdmin ||
            isMentor ||
            isInstructor ||
            isSupervisor,
        });
      } catch (error) {
        console.error("Admin panel yetki kontrolü hatası:", error);

        setAccess({
          isAdmin: false,
          isMentor: false,
          isInstructor: false,
          isSupervisor: false,
          canAccess: false,
        });
      }
    };

    checkAccess();
  }, []);

  // Yükleniyor
  if (access === null) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />

        <div className="p-6">
          Yükleniyor...
        </div>
      </main>
    );
  }

  // Yetkisiz
  if (!access.canAccess) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />

        <div className="p-10 text-center">
          <h2 className="text-xl font-semibold text-red-600">
            Bu sayfaya erişim yetkiniz yok
          </h2>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#eef5ff]">
      <InnovationHubHeader />

      <section className="p-6">
        <div className="mx-auto max-w-5xl space-y-6">

          {/* BAŞLIK */}

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <h1 className="text-2xl font-semibold text-[#173b7a]">
              Admin Panel
            </h1>

            <p className="mt-2 text-sm text-gray-500">
              Yönetim işlemlerinizi buradan gerçekleştirebilirsiniz.
            </p>
          </div>

          {/* YÖNETİM KARTLARI */}

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">

              {/* ============================= */}
              {/* SADECE ADMIN */}
              {/* ============================= */}

              {access.isAdmin && (
                <>
                  <AdminCard
                    icon={Newspaper}
                    title="Manşet Yönetimi"
                    onClick={() =>
                      navigate("/admin/banner")
                    }
                  />

                  <AdminCard
                    icon={Megaphone}
                    title="Duyuru Yönetimi"
                    onClick={() =>
                      navigate("/admin/announcements")
                    }
                  />

                  <AdminCard
                    icon={CalendarDays}
                    title="Etkinlikler"
                    onClick={() =>
                      navigate("/admin/events")
                    }
                  />

                  <AdminCard
                    icon={UsersRound}
                    title="Kullanıcı ve Takım Yönetimi"
                    onClick={() =>
                      navigate("/admin/users-teams")
                    }
                  />
                </>
              )}

              {/* ============================= */}
              {/* ADMIN + SUPERVISOR */}
              {/* ============================= */}

              {(access.isAdmin || access.isSupervisor) && (
                <AdminCard
                  icon={Shield}
                  title="Moderasyon"
                  onClick={() =>
                    navigate("/admin/moderation")
                  }
                />
              )}

              {/* ============================= */}
              {/* ADMIN + MENTOR */}
              {/* ============================= */}

              {(access.isAdmin || access.isMentor) && (
                <AdminCard
                  icon={CalendarClock}
                  title="Mentör Oturum Yönetimi"
                  onClick={() =>
                    navigate("/admin/mentor-sessions")
                  }
                />
              )}

              {/* ============================= */}
              {/* ADMIN + INSTRUCTOR */}
              {/* ============================= */}

              {(access.isAdmin || access.isInstructor) && (
                <AdminCard
                  icon={GraduationCap}
                  title="Eğitim Oturum Yönetimi"
                  onClick={() =>
                    navigate("/admin/training-sessions")
                  }
                />
              )}

            </div>
          </div>
        </div>
      </section>

      <InnovationHubFooter />
    </main>
  );
}

function AdminCard({ icon: Icon, title, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-h-[150px] flex-col items-center justify-center gap-4 rounded-2xl border border-blue-50 bg-[#f8fbff] p-6 text-center transition hover:-translate-y-1 hover:border-[#1565C0]/30 hover:bg-white hover:shadow-lg"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#e8f2ff] transition group-hover:bg-[#1565C0]">
        <Icon
          size={28}
          className="text-[#1565C0] transition group-hover:text-white"
        />
      </div>

      <h3 className="text-sm font-semibold text-[#173b7a]">
        {title}
      </h3>
    </button>
  );
}